import {computed, ref} from "vue";
import {useInfiniteQuery, useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import moment from "moment";
import {errorToast, successToast} from "@/composables/useToasts";

const filters = ref({
  sortBy: 'position',
  sortDirection: 'asc',
  search: '',
  dateFrom: null,
  dateTo: null,
});

export const competitorRanking = ref(null);
export const adjustments = ref([]);
export const dates = ref([]);

const getQueryKey = () => ['untracked-keywords', computed(() => ({
  sortBy: filters.value.sortBy,
  sortDirection: filters.value.sortDirection,
  search: filters.value.search,
  dateFrom: filters.value.dateFrom,
  dateTo: filters.value.dateTo,
}))];

export const useUntrackedKeywordsFilters = () => {
  const setSortBy = (sort) => {
    if (filters.value.sortBy === sort) {
      filters.value.sortDirection = filters.value.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }
    filters.value.sortBy = sort;
    filters.value.sortDirection = sort === 'keyword' ? 'asc' : 'desc';
  };

  const setSearch = (search) => {
    filters.value.search = search;
  };

  const setDateFrom = (dateFrom) => {
    filters.value.dateFrom = dateFrom;
  };

  const setDateTo = (dateTo) => {
    filters.value.dateTo = dateTo;
  };

  return {
    filters: computed(() => filters.value),
    sortBy: computed(() => filters.value.sortBy),
    sortDirection: computed(() => filters.value.sortDirection),
    dateFrom: computed(() => filters.value.dateFrom),
    dateTo: computed(() => filters.value.dateTo),
    setSortBy,
    setSearch,
    setDateFrom,
    setDateTo,
  };
};

export const useUntrackedKeywords = () => {
  const query = useInfiniteQuery({
    queryKey: getQueryKey(),
    queryFn: async ({pageParam}) => {

      const params = {
        sortBy: filters.value.sortBy,
        sortDirection: filters.value.sortDirection,
      };

      if (filters.value.search) {
        params.search = filters.value.search;
      }

      if (filters.value.dateFrom) {
        params.dateFrom = filters.value.dateFrom;
      }

      if (filters.value.dateTo) {
        params.dateTo = filters.value.dateTo;
      }

      const queryString = new URLSearchParams(params).toString();
      const url = pageParam
        ? `/ajax/keyword-scan?${queryString}&page=${pageParam}`
        : `/ajax/keyword-scan?${queryString}`;

      const {data} = await axios.get(url);

      competitorRanking.value = data.competitorRanking;
      adjustments.value = data.adjustments;
      dates.value = buildPaddedDates(data.dates);

      if (filters.value.dateFrom === null && data.dates?.length > 0 && data.dates[0]?.value) {
        filters.value.dateFrom = data.dates[0].value;
      }

      return data.untrackedKeywords;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.next_page_url) {
        return null;
      }

      const url = new URL(lastPage.next_page_url);
      const page = url.searchParams.get('page');
      return page ? parseInt(page, 10) : null;
    },
    gcTime: 120 * 60 * 1000,
    enabled: () => !!filters.value.dateFrom,
  });

  const keywords = computed(() => {
    if (!query.data.value?.pages) {
      return [];
    }
    return query.data.value.pages.flatMap((page) => page.data);
  });

  return {
    ...query,
    keywords,
    competitorRanking: computed(() => competitorRanking.value),
    adjustments: computed(() => adjustments.value),
    dates: computed(() => dates.value),
  };
};

export const useAddKeyword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (keyword) => {
      await axios.post('/ajax/keywords/update-keywords', {
        new_keywords: [keyword],
      });
    },
    onMutate: async (keyword) => {
      const queryKey = getQueryKey();

      await queryClient.cancelQueries({queryKey});

      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old?.pages) {
          return old;
        }

        return {
          ...old,
          pages: old.pages.map((page) => {
            const hasKeyword = page.data.some((k) => k.id === keyword.id);

            if (!hasKeyword) {
              return page;
            }

            return {
              ...page,
              data: page.data.filter((k) => k.id !== keyword.id),
              total: page.total - 1,
            };
          }),
        };
      });

      successToast('Keyword added successfully.');

      return {previousData, queryKey};
    },
    onError: (error, keyword, context) => {
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      errorToast('Something went wrong adding the keyword.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['untracked-keywords']});
    }
  });
};

const buildPaddedDates = (rawDates) => {
  if (!rawDates || !Array.isArray(rawDates) || rawDates.length === 0) {
    return rawDates;
  }

  const earliestDate = moment(rawDates[rawDates.length - 1].value).startOf('month');
  const previousMonth = moment().subtract(1, 'month').startOf('month');
  const existingMonths = new Set(rawDates.map(d => moment(d.value).format('YYYY-MM')));

  const result = [];
  const cursor = previousMonth.clone();

  while (cursor.isSameOrAfter(earliestDate, 'month')) {
    const monthKey = cursor.format('YYYY-MM');

    if (existingMonths.has(monthKey)) {
      result.push(rawDates.find(date => moment(date.value).format('YYYY-MM') === monthKey));
    } else {
      result.push({
        value: cursor.format('YYYY-MM-DD'),
        label: cursor.format('MMMM YYYY') + ' - (No results)',
        disabled: true,
      });
    }

    cursor.subtract(1, 'month');
  }

  return result;
};
